import {
	Drawer,
	Box,
	Typography,
	IconButton,
	Divider,
	TextField,
	CircularProgress,
	Paper,
	List,
	ListItemButton,
	ListItemText,
	Tooltip,
	Button,
	Chip,
	Card,
	CardContent,
	CardActionArea
} from '@mui/material';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAskChatMutation, useLazyGetChatMessagesQuery } from '@/store/services/chat';
import { useGetDocumentsListQuery } from '@/store/services/documents';
import UploadDocumentDialog from '@/pages/apps/documents/documents/UploadDocumentDialog';
import { getUser } from '@/utils/localStorage';
import type { ChatAddress, ChatMessage, ChatDocument } from './types';

type ChatBoxDialogProps = {
	open: boolean;
	selectedAddress: ChatAddress | null;
	addresses?: ChatAddress[];
	isAddressesLoading?: boolean;
	onSelectAddress?: (address: ChatAddress) => void;
	onBackToAddressSelection?: () => void;
	onClose: () => void;
	/** True while the launcher is resolving which address to use (drawer opens immediately) */
	isAddressResolving?: boolean;
};

const CHAT_PENDING_KEY = 'chat_pending_by_address_v1';
const CHAT_PENDING_TIMEOUT_MS = 60000;
const CHAT_PENDING_POLL_MS = 3000;
const CHAT_UPLOAD_DOC_POLL_MS = 3000;
const CHAT_UPLOAD_DOC_TIMEOUT_MS = 120000;

/** Attach / send icons in the composer footer */
const COMPOSER_ACTION_ICON_BUTTON_SX = {
	borderRadius: '6px',
	border: '1px solid rgba(0, 0, 0, 0.18)',
	color: '#000',
	width: 32,
	height: 32,
	transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
	'& svg': {
		transition: 'transform 0.18s ease'
	},
	'&:hover:not(.Mui-disabled)': {
		backgroundColor: 'rgba(43, 127, 255, 0.1)',
		borderColor: 'rgba(43, 127, 255, 0.38)',
		color: '#1d4ed8',
		boxShadow: '0 1px 6px rgba(43, 127, 255, 0.22)',
		'& svg': {
			transform: 'scale(1.06)'
		}
	}
} as const;

const CHATBOX_INPUT_SHADOW_IDLE =
	'0 0 0 1px rgba(147, 197, 253, 0.45), 0 2px 10px rgba(43, 127, 255, 0.14), 0 8px 30px rgba(56, 189, 248, 0.14), 0 0 40px rgba(59, 130, 246, 0.08)';
const CHATBOX_INPUT_SHADOW_ACTIVE =
	'0 0 0 1px rgba(147, 197, 253, 0.28), 0 1px 6px rgba(43, 127, 255, 0.09), 0 4px 18px rgba(56, 189, 248, 0.09)';
const SUPPORT_EMAIL = 'support@assetsid.com';
const SUPPORT_MAIL_SUBJECT = 'AssetsID - AI Chat Support Inquiry';

const stripHtmlToText = (html: string) => {
	if (!html || typeof window === 'undefined') {
		return html || '';
	}
	const doc = new DOMParser().parseFromString(html, 'text/html');
	return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

type SupportMailtoContext = {
	addressId?: number | null;
	conversationId?: number | null;
	question: string;
};

const extractConversationId = (response: any): number | null => {
	const candidates = [
		response?.data?.conversation_id,
		response?.data?.conversation?.id,
		response?.data?.items?.conversation_id,
		response?.data?.conversationId
	];

	for (const candidate of candidates) {
		const num = Number(candidate);
		if (Number.isFinite(num) && num > 0) {
			return num;
		}
	}

	const list = response?.data?.items?.data;
	if (Array.isArray(list)) {
		for (const item of list) {
			const num = Number(item?.conversation_id ?? item?.conversation?.id);
			if (Number.isFinite(num) && num > 0) {
				return num;
			}
		}
	}

	return null;
};

const buildSupportMailBody = ({
	addressId,
	conversationId,
	question
}: SupportMailtoContext) => {
	const lines: string[] = [
		'Hi Support,',
		'',
		'I asked a question but the system could not find an answer in my uploaded documents.',
		''
	];

	if (addressId != null && Number.isFinite(addressId)) {
		lines.push(`Address ID: ${addressId}`);
	}
	if (conversationId != null && Number.isFinite(conversationId)) {
		lines.push(`Conversation ID: ${conversationId}`);
	}
	if (question.trim()) {
		lines.push(`Question: ${question.trim()}`);
	}

	lines.push('', 'Thanks');

	return lines.join('\n');
};

const buildSupportMailtoHref = (context: SupportMailtoContext) => {
	const userEmail = String(getUser()?.email || '').trim();
	const parts = [`subject=${encodeURIComponent(SUPPORT_MAIL_SUBJECT)}`];
	const body = buildSupportMailBody(context);

	if (body) {
		parts.push(`body=${encodeURIComponent(body)}`);
	}
	if (userEmail) {
		parts.push(`from=${encodeURIComponent(userEmail)}`);
	}
	return `mailto:${SUPPORT_EMAIL}?${parts.join('&')}`;
};

const getPrecedingUserQuestion = (
	allMessages: ChatMessage[],
	assistantMessage: ChatMessage
) => {
	const index = allMessages.findIndex((item) => String(item.id) === String(assistantMessage.id));
	if (index <= 0) {
		return '';
	}
	for (let i = index - 1; i >= 0; i -= 1) {
		if (allMessages[i]?.role === 'user') {
			return stripHtmlToText(allMessages[i].content || '');
		}
	}
	return '';
};

type PendingEntry = {
	question: string;
	startedAt: string;
};

function ChatBoxDialog({
	open,
	selectedAddress,
	addresses = [],
	isAddressesLoading = false,
	onSelectAddress,
	onBackToAddressSelection,
	onClose,
	isAddressResolving = false
}: ChatBoxDialogProps) {
	const [messageText, setMessageText] = useState('');
	const [isComposerFocused, setIsComposerFocused] = useState(false);
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [page, setPage] = useState(1);
	const [hasMore, setHasMore] = useState(false);
	const [isInitialLoading, setIsInitialLoading] = useState(false);
	const [loadedAddressId, setLoadedAddressId] = useState<number | null>(null);
	const [conversationId, setConversationId] = useState<number | null>(null);
	const [isPaginating, setIsPaginating] = useState(false);
	const [predefinedQuestions, setPredefinedQuestions] = useState<string[]>([]);
	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
	const [selectedDocuments, setSelectedDocuments] = useState<ChatDocument[]>([]);
	const [pendingUploadedFiles, setPendingUploadedFiles] = useState<string[]>([]);
	const [pendingUploadedDocIds, setPendingUploadedDocIds] = useState<number[]>([]);
	const [uploadedFilesPollStartedAt, setUploadedFilesPollStartedAt] = useState<number | null>(null);
	const [mentionAnchorVisible, setMentionAnchorVisible] = useState(false);
	const [activeMentionIndex, setActiveMentionIndex] = useState(0);
	const [isAwaitingAnswer, setIsAwaitingAnswer] = useState(false);
	const [loadingPredefinedQuestionKey, setLoadingPredefinedQuestionKey] = useState<string | null>(null);
	const listRef = useRef<HTMLDivElement | null>(null);
	const mentionListRef = useRef<HTMLUListElement | null>(null);
	const pendingPollRef = useRef<number | null>(null);
	const uploadedDocsPollRef = useRef<number | null>(null);
	const hasShownUploadPendingToastRef = useRef(false);
	const navigate = useNavigate();
	const [fetchMessages] = useLazyGetChatMessagesQuery();
	const [askChat, { isLoading: isSending }] = useAskChatMutation();
	const { data: documentsResponse, refetch: refetchDocuments } = useGetDocumentsListQuery({});

	const addressText = selectedAddress
    ? selectedAddress.name || selectedAddress.location_name || "Address"
    : "Address";

	const isChatContentStale =
		Boolean(selectedAddress?.id) && loadedAddressId !== selectedAddress?.id;
	const isChatHistoryLoading =
		isAddressResolving || isInitialLoading || isChatContentStale;
	const shouldShowAddressSelection = !selectedAddress;
	const canGoBackToAddressSelection =
		!shouldShowAddressSelection && addresses.length > 1 && Boolean(onBackToAddressSelection);

	const isComposerActive = isComposerFocused || messageText.length > 0;

	const allAddressDocuments = useMemo(() => {
		const allDocuments = (documentsResponse?.data ?? []) as ChatDocument[];
		if (!selectedAddress?.id) {
			return [];
		}

		const isCompletedStatus = (status?: string, isEnabled?: number) => {
			const normalized = String(status || '').trim().toLowerCase();
			if (['chunked', 'extracted', 'failed'].includes(normalized)) {
				return false;
			}
			return normalized === 'completed';
		};

		return allDocuments.filter(
			(doc) =>
				Number(doc?.address?.id) === Number(selectedAddress.id) &&
				isCompletedStatus(doc?.processing_status, doc?.is_enabled)
		);
	}, [documentsResponse, selectedAddress?.id]);

	const allAddressDocumentsRaw = useMemo(() => {
		const allDocuments = (documentsResponse?.data ?? []) as ChatDocument[];
		if (!selectedAddress?.id) {
			return [];
		}
		return allDocuments.filter(
			(doc) => Number(doc?.address?.id) === Number(selectedAddress.id)
		);
	}, [documentsResponse, selectedAddress?.id]);

	const selectedDocumentIds = useMemo(
		() => selectedDocuments.map((document) => document.id),
		[selectedDocuments]
	);

	const mentionQuery = useMemo(() => {
		const atIndex = messageText.lastIndexOf('@');
		if (atIndex < 0) {
			return null;
		}
		const afterAt = messageText.slice(atIndex + 1);
		if (afterAt.includes(' ') || afterAt.includes('\n')) {
			return null;
		}
		return afterAt.trim().toLowerCase();
	}, [messageText]);

	const filteredMentionDocuments = useMemo(() => {
		if (mentionQuery === null) {
			return [];
		}
		const selectedIds = new Set(selectedDocuments.map((doc) => doc.id));
		return allAddressDocuments
			.filter((doc) => !selectedIds.has(doc.id))
			.filter((doc) =>
				mentionQuery.length === 0
					? true
					: (doc.file_name || doc.name || '').toLowerCase().includes(mentionQuery)
			)
			.slice(0, 8);
	}, [mentionQuery, allAddressDocuments, selectedDocuments]);

	const normalizeLoose = (value: string) => value.toLowerCase().replace(/[\s_-]+/g, '');

	useEffect(() => {
		if (
			!selectedAddress?.id ||
			(pendingUploadedFiles.length === 0 && pendingUploadedDocIds.length === 0) ||
			allAddressDocuments.length === 0
		) {
			return;
		}

		const normalizedPending = pendingUploadedFiles.map(normalizeLoose);
		const matches = allAddressDocuments.filter((doc) => {
			if (pendingUploadedDocIds.includes(Number(doc.id))) {
				return true;
			}
			const n1 = normalizeLoose(doc.file_name || '');
			const n2 = normalizeLoose(doc.name || '');
			return normalizedPending.some((p) => p === n1 || p === n2 || n1.includes(p) || p.includes(n1));
		});

		if (matches.length === 0) {
			return;
		}

		setSelectedDocuments((prev) => {
			const ids = new Set(prev.map((d) => d.id));
			const next = [...prev];
			matches.forEach((m) => {
				if (!ids.has(m.id)) next.push(m);
			});
			return next;
		});

		toast.success('Document is completed and attached automatically.');
		setPendingUploadedFiles([]);
		setPendingUploadedDocIds([]);
		setUploadedFilesPollStartedAt(null);
		hasShownUploadPendingToastRef.current = false;
	}, [pendingUploadedFiles, pendingUploadedDocIds, allAddressDocuments, selectedAddress?.id]);

	useEffect(() => {
		if (!selectedAddress?.id || pendingUploadedDocIds.length === 0 || allAddressDocumentsRaw.length === 0) {
			return;
		}

		const hasFailedUploadedDoc = allAddressDocumentsRaw.some(
			(doc) =>
				pendingUploadedDocIds.includes(Number(doc.id)) &&
				String(doc.processing_status || '').trim().toLowerCase() === 'failed'
		);

		if (!hasFailedUploadedDoc) {
			return;
		}

		toast.error('Uploaded document failed to process.');
		setPendingUploadedFiles([]);
		setPendingUploadedDocIds([]);
		setUploadedFilesPollStartedAt(null);
		hasShownUploadPendingToastRef.current = false;
		if (uploadedDocsPollRef.current) {
			window.clearInterval(uploadedDocsPollRef.current);
			uploadedDocsPollRef.current = null;
		}
	}, [pendingUploadedDocIds, allAddressDocumentsRaw, selectedAddress?.id]);

	useEffect(() => {
		if (
			!selectedAddress?.id ||
			(pendingUploadedFiles.length === 0 && pendingUploadedDocIds.length === 0)
		) {
			if (uploadedDocsPollRef.current) {
				window.clearInterval(uploadedDocsPollRef.current);
				uploadedDocsPollRef.current = null;
			}
			return;
		}

		if (!uploadedFilesPollStartedAt) {
			setUploadedFilesPollStartedAt(Date.now());
		}

		if (!hasShownUploadPendingToastRef.current) {
			toast.info('Document is processing. It will attach automatically once completed.');
			hasShownUploadPendingToastRef.current = true;
		}

		if (!uploadedDocsPollRef.current) {
			uploadedDocsPollRef.current = window.setInterval(() => {
				const startedAt = uploadedFilesPollStartedAt ?? Date.now();
				if (Date.now() - startedAt > CHAT_UPLOAD_DOC_TIMEOUT_MS) {
					if (uploadedDocsPollRef.current) {
						window.clearInterval(uploadedDocsPollRef.current);
						uploadedDocsPollRef.current = null;
					}
					toast.warning('Document is still processing. Please try again shortly.');
					setPendingUploadedFiles([]);
					setPendingUploadedDocIds([]);
					setUploadedFilesPollStartedAt(null);
					hasShownUploadPendingToastRef.current = false;
					return;
				}
				void refetchDocuments();
			}, CHAT_UPLOAD_DOC_POLL_MS);
		}

		return () => {
			if (uploadedDocsPollRef.current) {
				window.clearInterval(uploadedDocsPollRef.current);
				uploadedDocsPollRef.current = null;
			}
		};
	}, [
		pendingUploadedFiles,
		pendingUploadedDocIds,
		selectedAddress?.id,
		refetchDocuments,
		uploadedFilesPollStartedAt
	]);

	const mapApiMessages = (
		response: any,
		addressId: number,
		activeConversationId: number | null
	): ChatMessage[] => {
		const list = response?.data?.items?.data || [];
		if (!Array.isArray(list)) {
			return [];
		}
		const mapped = [...list]
			.reverse()
			.map((item: any, index: number) => {
				const role = item?.role === 'assistant' ? 'assistant' : 'user';
				const content = item?.content || '';
				if (!content) return null;
				return {
					id: item?.id ?? `${role}-${index}-${content.slice(0, 10)}`,
					role,
					content,
					createdAt: item?.created_at,
					documents: Array.isArray(item?.documents) ? item.documents : []
				} as ChatMessage;
			})
			.filter(Boolean) as ChatMessage[];

		return mapped.map((message) => {
			if (
				message.role !== 'assistant' ||
				!message.content.toLowerCase().includes(SUPPORT_EMAIL)
			) {
				return message;
			}

			const question = getPrecedingUserQuestion(mapped, message);
			const mailContext: SupportMailtoContext = {
				addressId,
				conversationId: activeConversationId,
				question
			};
			return {
				...message,
				support: {
					email: SUPPORT_EMAIL,
					subject: SUPPORT_MAIL_SUBJECT,
					body: buildSupportMailBody(mailContext),
					mailto: buildSupportMailtoHref(mailContext)
				}
			};
		});
	};

	const formatMessageTime = (dateInput?: string) => {
		if (!dateInput) {
			return '';
		}

		const normalized = dateInput.includes(' ') ? dateInput.replace(' ', 'T') : dateInput;
		const date = new Date(normalized);
		if (Number.isNaN(date.getTime())) {
			return '';
		}

		const now = new Date();
		const isToday =
			date.getFullYear() === now.getFullYear() &&
			date.getMonth() === now.getMonth() &&
			date.getDate() === now.getDate();

		if (!isToday) {
			return date.toLocaleString([], {
				day: '2-digit',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		}

		return date.toLocaleTimeString([], {
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const enhanceAssistantHtml = (rawContent: string, mailtoHref: string) => {
		if (!rawContent || typeof window === 'undefined') {
			return '';
		}

		const parser = new DOMParser();
		const document = parser.parseFromString(rawContent, 'text/html');

		// Strip active content and unsafe attributes before rendering.
		document.querySelectorAll('script, style, iframe, object, embed').forEach((node) => node.remove());
		document.querySelectorAll<HTMLElement>('*').forEach((element) => {
			Array.from(element.attributes).forEach((attribute) => {
				const attrName = attribute.name.toLowerCase();
				const attrValue = attribute.value.trim().toLowerCase();
				if (attrName.startsWith('on')) {
					element.removeAttribute(attribute.name);
					return;
				}
				if ((attrName === 'href' || attrName === 'src') && attrValue.startsWith('javascript:')) {
					element.removeAttribute(attribute.name);
				}
			});
		});

		// Turn plain support email into clickable mailto links when not already linked.
		const emailRegex = /\bsupport@assetsid\.com\b/gi;
		const textNodes: Text[] = [];
		const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);

		let node = walker.nextNode();
		while (node) {
			textNodes.push(node as Text);
			node = walker.nextNode();
		}

		textNodes.forEach((textNode) => {
			const parent = textNode.parentElement;
			if (!parent || parent.closest('a')) {
				return;
			}

			const text = textNode.textContent || '';
			if (!emailRegex.test(text)) {
				return;
			}
			emailRegex.lastIndex = 0;

			const fragment = document.createDocumentFragment();
			let lastIndex = 0;
			text.replace(emailRegex, (match, offset: number) => {
				if (offset > lastIndex) {
					fragment.appendChild(document.createTextNode(text.slice(lastIndex, offset)));
				}
				const anchor = document.createElement('a');
				anchor.href = mailtoHref;
				anchor.textContent = match;
				anchor.style.textDecoration = 'underline';
				anchor.style.wordBreak = 'break-all';
				// anchor.style.color = 'inherit';
				fragment.appendChild(anchor);
				lastIndex = offset + match.length;
				return match;
			});

			if (lastIndex < text.length) {
				fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
			}
			parent.replaceChild(fragment, textNode);
		});

		document.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((anchor) => {
			const href = (anchor.getAttribute('href') || '').toLowerCase();
			if (href.includes(SUPPORT_EMAIL)) {
				anchor.setAttribute('href', mailtoHref);
			}
		});

		return document.body.innerHTML;
	};

	const getSupportMailtoHref = (message: ChatMessage) => {
		const question =
			getPrecedingUserQuestion(messages, message) ||
			message.support?.body?.trim() ||
			'';

		return buildSupportMailtoHref({
			addressId: selectedAddress?.id ?? null,
			conversationId,
			question
		});
	};

	const renderAssistantMessageContent = (message: ChatMessage) => {
		const content = message.content || '';
		const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(content);
		const hasSupportEmail = content.toLowerCase().includes(SUPPORT_EMAIL);
		const mailtoHref = hasSupportEmail ? getSupportMailtoHref(message) : '';

		if (!looksLikeHtml && !hasSupportEmail) {
			return (
				<Typography variant="body2" className="whitespace-pre-wrap break-words" sx={{ color: '#000' }}>
					{content}
				</Typography>
			);
		}

		if (!looksLikeHtml && hasSupportEmail) {
			const [before, after] = content.split(SUPPORT_EMAIL);
			return (
				<Typography variant="body2" className="whitespace-pre-wrap break-words" sx={{ color: '#000' }}>
					{before}
					<Box
						component="a"
						href={mailtoHref}
						sx={{
							color: '#155dfc',
							textDecoration: 'underline',
							wordBreak: 'break-all'
						}}
					>
						{SUPPORT_EMAIL}
					</Box>
					{after}
				</Typography>
			);
		}

		const enhancedHtml = enhanceAssistantHtml(content, mailtoHref);
		if (!enhancedHtml) {
			return (
				<Typography variant="body2" className="whitespace-pre-wrap break-words" sx={{ color: '#000' }}>
					{content}
				</Typography>
			);
		}

		return (
			<Box
				sx={{
					color: '#000',
					fontSize: '0.875rem',
					lineHeight: 1.5,
					wordBreak: 'break-word',
					'& p': { margin: 0, whiteSpace: 'pre-wrap', mb: 1.5 },
					'& ul, & ol': {
						mb: 2,
						pl: 4,
						listStyleType: 'disc',
						listStylePosition: 'outside'
					},
					'& a': {
						color: '#155dfc !important',
						textDecoration: 'underline',
						wordBreak: 'break-all',
						transition: 'all 0.35s ease',
						'&:hover': {
							opacity: 0.7
						},
						'&:visited': {
							color: '#155dfc !important',
							opacity: 0.7
						}
					}
				}}
				dangerouslySetInnerHTML={{ __html: enhancedHtml }}
			/>
		);
	};

	const normalizeFileToken = (value: string) =>
		value.toLowerCase().replace(/[\s_-]+/g, '');

	const getDocumentName = (document: ChatDocument) => document.file_name || document.name || '';
	const getDocumentUrl = (document: ChatDocument) => document.document_file_url || document.url || '';

	const getFileNameFromUrl = (url: string) => {
		try {
			const pathname = new URL(url).pathname;
			const name = pathname.split('/').pop() || '';
			return decodeURIComponent(name);
		} catch {
			return '';
		}
	};

	const renderUserMessageContent = (message: ChatMessage) => {
		const allDocuments = message.documents || [];
		const documents = allDocuments.filter((doc) => !doc.deleted && Boolean(getDocumentUrl(doc)));
		const deletedDocuments = allDocuments.filter((doc) => Boolean(doc.deleted));
		const renderDeletedDocuments = () => {
			if (deletedDocuments.length === 0) {
				return null;
			}

			return (
				<Box className="mb-1 flex flex-wrap gap-1">
					{deletedDocuments.map((document) => (
						<Tooltip
							key={`deleted-${document.id}`}
							title="Document is deleted and no longer available."
							arrow
						>
							<span>
								<Chip
									size="small"
									disabled
									label={`${getDocumentName(document) || 'Document deleted'} (Deleted)`}
									icon={<FuseSvgIcon size={14}>heroicons-outline:trash</FuseSvgIcon>}
									sx={{
										maxWidth: 260,
										opacity: 0.8,
										'& .MuiChip-label': {
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap'
										}
									}}
								/>
							</span>
						</Tooltip>
					))}
				</Box>
			);
		};

		if (documents.length === 0 && deletedDocuments.length === 0) {
			return (
				<Typography variant="body2" className="whitespace-pre-wrap break-words" sx={{ color: '#000' }}>
					{message.content}
				</Typography>
			);
		}

		const content = message.content || '';
		const isImageDocument = (doc: ChatDocument) => {
			const name = getDocumentName(doc);
			const urlName = getFileNameFromUrl(getDocumentUrl(doc));
			const fileType = (doc.file_type || '').toLowerCase();
			const target = `${name} ${urlName}`.toLowerCase();
			return (
				/\.(png|jpe?g|webp|gif|bmp|svg)$/.test(target) ||
				['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp', 'svg'].includes(fileType)
			);
		};
		const imageDocuments = documents.filter(isImageDocument);
		const nonImageDocuments = documents.filter((doc) => !isImageDocument(doc));
		const tokenRegex = /\b[\w.-]+\.[A-Za-z0-9]{2,8}\b/g;
		const tokens = Array.from(new Set(content.match(tokenRegex) || []));
		const tokenToUrl = new Map<string, string>();

		tokens.forEach((token) => {
			const normalizedToken = normalizeFileToken(token);
			const matchedDoc = nonImageDocuments.find((doc) => {
				const docName = getDocumentName(doc);
				const urlName = getFileNameFromUrl(getDocumentUrl(doc));
				const aliases = [docName, urlName].filter(Boolean).map(normalizeFileToken);
				return aliases.some(
					(alias) => alias === normalizedToken || alias.includes(normalizedToken) || normalizedToken.includes(alias)
				);
			});
			if (matchedDoc) {
				tokenToUrl.set(token, getDocumentUrl(matchedDoc));
			}
		});

		const matchedTokens = Array.from(tokenToUrl.keys());
		const hasInlineMatches = matchedTokens.length > 0;
		const emphasizedTokens = Array.from(new Set(tokens));

		let inlineContent: React.ReactNode = (
			<Typography variant="body2" className="whitespace-pre-wrap break-words" sx={{ color: '#000' }}>
				{content}
			</Typography>
		);

		if (emphasizedTokens.length > 0) {
			const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
			const splitter = new RegExp(`(${emphasizedTokens.map(escapeRegex).join('|')})`, 'g');
			const parts = content.split(splitter);

			inlineContent = (
				<Typography variant="body2" className="whitespace-pre-wrap break-words" sx={{ color: '#000' }}>
					{parts.map((part, index) => {
						const href = tokenToUrl.get(part);
						if (!href) {
							const isFileToken = emphasizedTokens.includes(part);
							if (!isFileToken) {
								return <span key={`${part}-${index}`}>{part}</span>;
							}
							return (
								<Box
									key={`${part}-${index}`}
									component="span"
									sx={{
										fontWeight: 700,
										backgroundColor: 'rgba(255, 235, 59, 0.35)',
										borderRadius: '4px',
										px: 0.35
									}}
								>
									{part}
								</Box>
							);
						}
						return (
							<Box
								key={`${part}-${index}`}
								component="a"
								href={href}
								target="_blank"
								rel="noopener noreferrer"
								sx={{
									color: '#000',
									textDecoration: 'underline',
									fontWeight: 700,
									backgroundColor: 'rgba(255, 235, 59, 0.3)',
									borderRadius: '4px',
									px: 0.35,
									cursor: 'pointer'
								}}
							>
								{part}
							</Box>
						);
					})}
				</Typography>
			);
		}

		if (hasInlineMatches) {
			return (
				<Box>
					{renderDeletedDocuments()}
					{imageDocuments.length > 0 && (
						<Box className="mb-2 flex flex-wrap gap-2">
							{imageDocuments.map((document) => (
								<Box
									key={document.id}
									component="a"
									href={getDocumentUrl(document)}
									target="_blank"
									rel="noopener noreferrer"
									sx={{
										display: 'inline-flex',
										borderRadius: 1,
										overflow: 'hidden',
										border: '1px solid rgba(0,0,0,0.14)'
									}}
								>
									<Box
										component="img"
										src={getDocumentUrl(document)}
										alt={getDocumentName(document) || 'attachment-image'}
										loading="lazy"
										sx={{
											width: 120,
											height: 80,
											objectFit: 'cover',
											display: 'block'
										}}
									/>
								</Box>
							))}
						</Box>
					)}
					{inlineContent}
				</Box>
			);
		}

		return (
			<Box>
				{renderDeletedDocuments()}
				{imageDocuments.length > 0 && (
					<Box className="mb-2 flex flex-wrap gap-2">
						{imageDocuments.map((document) => (
							<Box
								key={document.id}
								component="a"
								href={getDocumentUrl(document)}
								target="_blank"
								rel="noopener noreferrer"
								sx={{
									display: 'inline-flex',
									borderRadius: 1,
									overflow: 'hidden',
									border: '1px solid rgba(0,0,0,0.14)'
								}}
							>
								<Box
									component="img"
									src={getDocumentUrl(document)}
									alt={getDocumentName(document) || 'attachment-image'}
									loading="lazy"
									sx={{
										width: 120,
										height: 80,
										objectFit: 'cover',
										display: 'block'
									}}
								/>
							</Box>
						))}
					</Box>
				)}
				{nonImageDocuments.length > 0 && (
					<Box className="mb-1 flex flex-wrap gap-1">
						{nonImageDocuments.map((document) => (
							<Box
								key={document.id}
								component="a"
								href={getDocumentUrl(document)}
								target="_blank"
								rel="noopener noreferrer"
								sx={{
									fontSize: '11px',
									color: '#000',
									textDecoration: 'underline',
									cursor: 'pointer',
									wordBreak: 'break-all'
								}}
							>
								{getDocumentName(document) || getFileNameFromUrl(getDocumentUrl(document))}
							</Box>
						))}
					</Box>
				)}
				{inlineContent}
			</Box>
		);
	};

	const getHasMore = (response: any) => {
		const items = response?.data?.items;
		if (!items) {
			return false;
		}
		return Boolean(items.next_page_url);
	};

	const readPendingMap = (): Record<string, PendingEntry> => {
		if (typeof window === 'undefined') return {};
		try {
			const raw = sessionStorage.getItem(CHAT_PENDING_KEY);
			return raw ? (JSON.parse(raw) as Record<string, PendingEntry>) : {};
		} catch {
			return {};
		}
	};

	const writePendingMap = (map: Record<string, PendingEntry>) => {
		if (typeof window === 'undefined') return;
		try {
			sessionStorage.setItem(CHAT_PENDING_KEY, JSON.stringify(map));
		} catch {
			// ignore storage errors
		}
	};

	const setPendingForAddress = (addressId: number, entry?: PendingEntry) => {
		const map = readPendingMap();
		const key = String(addressId);
		if (!entry) {
			delete map[key];
		} else {
			map[key] = entry;
		}
		writePendingMap(map);
	};

	const getPendingForAddress = (addressId: number): PendingEntry | null => {
		const map = readPendingMap();
		return map[String(addressId)] || null;
	};

	const parseApiDate = (value?: string) => {
		if (!value) return null;
		const normalized = value.includes(' ') ? value.replace(' ', 'T') : value;
		const date = new Date(normalized);
		return Number.isNaN(date.getTime()) ? null : date;
	};

	const hasAssistantAfterPendingQuestion = (response: any, pending: PendingEntry) => {
		const list = response?.data?.items?.data;
		if (!Array.isArray(list) || list.length === 0) return false;
		const ordered = [...list].reverse(); // oldest -> newest
		const pendingTime = new Date(pending.startedAt).getTime();
		let userIndex = -1;

		for (let i = ordered.length - 1; i >= 0; i -= 1) {
			const row = ordered[i];
			if (row?.role !== 'user') continue;
			if ((row?.content || '').trim() !== pending.question.trim()) continue;
			const rowTime = parseApiDate(row?.created_at)?.getTime() ?? 0;
			if (rowTime + 1000 >= pendingTime) {
				userIndex = i;
				break;
			}
		}

		if (userIndex < 0) return false;
		for (let i = userIndex + 1; i < ordered.length; i += 1) {
			if (ordered[i]?.role === 'assistant') {
				return true;
			}
		}
		return false;
	};

	const resetChatState = () => {
		setLoadedAddressId(null);
		setConversationId(null);
		setMessages([]);
		setPage(1);
		setHasMore(false);
		setPredefinedQuestions([]);
		setSelectedDocuments([]);
		setMentionAnchorVisible(false);
		setIsAwaitingAnswer(false);
		setLoadingPredefinedQuestionKey(null);
	};

	const loadMessages = async (
		targetPage: number,
		mode: 'initial' | 'paginate',
		addressId: number
	) => {
		const previousScrollHeight = listRef.current?.scrollHeight ?? 0;
		const previousScrollTop = listRef.current?.scrollTop ?? 0;

		if (mode === 'initial') {
			setIsInitialLoading(true);
		} else {
			setIsPaginating(true);
		}

		try {
			const response = await fetchMessages({
				address_id: addressId,
				page: targetPage
			}).unwrap();

			if (selectedAddress?.id !== addressId) {
				return;
			}

			const nextConversationId = extractConversationId(response);
			if (nextConversationId != null) {
				setConversationId(nextConversationId);
			}
			const mapped = mapApiMessages(
				response,
				addressId,
				nextConversationId ?? conversationId
			);
			const questions = response?.data?.predefined_questions;
			setPredefinedQuestions(Array.isArray(questions) ? questions.filter(Boolean) : []);

			setMessages((prev) => {
				if (mode === 'initial') {
					return mapped;
				}
				const ids = new Set(prev.map((message) => String(message.id)));
				const older = mapped.filter((message) => !ids.has(String(message.id)));
				return [...older, ...prev];
			});
			setPage(targetPage);
			setHasMore(getHasMore(response));

			if (mode === 'initial') {
				setLoadedAddressId(addressId);
			}

			requestAnimationFrame(() => {
				if (!listRef.current || selectedAddress?.id !== addressId) {
					return;
				}

				if (mode === 'paginate') {
					const newScrollHeight = listRef.current.scrollHeight;
					listRef.current.scrollTop =
						newScrollHeight - previousScrollHeight + previousScrollTop;
					return;
				}

				listRef.current.scrollTop = listRef.current.scrollHeight;
			});
		} catch (error) {
			// Keep UI stable; errors are intentionally silent in phase 1 shell.
		} finally {
			if (selectedAddress?.id !== addressId) {
				return;
			}
			if (mode === 'initial') {
				setLoadedAddressId(addressId);
				setIsInitialLoading(false);
			} else {
				setIsPaginating(false);
			}
		}
	};

	useLayoutEffect(() => {
		if (!open) {
			resetChatState();
			setIsInitialLoading(false);
			return;
		}

		if (!selectedAddress?.id) {
			return;
		}

		resetChatState();
		setIsInitialLoading(true);
		void loadMessages(1, 'initial', selectedAddress.id);
	}, [open, selectedAddress?.id]);

	useEffect(() => {
		if (!open || !selectedAddress?.id) {
			return;
		}

		const pending = getPendingForAddress(selectedAddress.id);
		if (!pending) {
			return;
		}

		setIsAwaitingAnswer(true);

		const checkPending = async () => {
			const pendingNow = getPendingForAddress(selectedAddress.id as number);
			if (!pendingNow) {
				setIsAwaitingAnswer(false);
				return true;
			}

			const pendingAge = Date.now() - new Date(pendingNow.startedAt).getTime();
			if (pendingAge > CHAT_PENDING_TIMEOUT_MS) {
				setPendingForAddress(selectedAddress.id as number);
				setIsAwaitingAnswer(false);
				return true;
			}

			try {
				const response = await fetchMessages({
					address_id: selectedAddress.id,
					page: 1
				}).unwrap();
				const nextConversationId = extractConversationId(response);
				if (nextConversationId != null) {
					setConversationId(nextConversationId);
				}
				const mapped = mapApiMessages(
					response,
					selectedAddress.id as number,
					nextConversationId ?? conversationId
				);
				const questions = response?.data?.predefined_questions;
				setPredefinedQuestions(Array.isArray(questions) ? questions.filter(Boolean) : []);
				setMessages(mapped);
				setLoadedAddressId(selectedAddress.id as number);

				const resolved = hasAssistantAfterPendingQuestion(response, pendingNow);
				if (resolved) {
					setPendingForAddress(selectedAddress.id as number);
					setIsAwaitingAnswer(false);
					return true;
				}
			} catch {
				// keep waiting
			}

			return false;
		};

		void checkPending();
		pendingPollRef.current = window.setInterval(async () => {
			const done = await checkPending();
			if (done && pendingPollRef.current) {
				window.clearInterval(pendingPollRef.current);
				pendingPollRef.current = null;
			}
		}, CHAT_PENDING_POLL_MS);

		return () => {
			if (pendingPollRef.current) {
				window.clearInterval(pendingPollRef.current);
				pendingPollRef.current = null;
			}
		};
	}, [open, selectedAddress?.id, fetchMessages]);

	useEffect(() => {
		setMentionAnchorVisible(mentionQuery !== null);
	}, [mentionQuery]);

	useEffect(() => {
		if (!mentionAnchorVisible || filteredMentionDocuments.length === 0) {
			setActiveMentionIndex(0);
			return;
		}
		setActiveMentionIndex((prev) => Math.min(prev, filteredMentionDocuments.length - 1));
	}, [mentionAnchorVisible, filteredMentionDocuments]);

	const handleSelectMentionDocument = (document: ChatDocument) => {
		setSelectedDocuments((prev) => {
			if (prev.some((item) => item.id === document.id)) {
				return prev;
			}
			return [...prev, document];
		});

		const atIndex = messageText.lastIndexOf('@');
		if (atIndex >= 0) {
			const beforeMention = messageText.slice(0, atIndex).replace(/\s+$/, '');
			setMessageText(beforeMention.length > 0 ? `${beforeMention} ` : "");
		}
		setMentionAnchorVisible(false);
		setActiveMentionIndex(0);
	};

	const handleRemoveSelectedDocument = (document: ChatDocument) => {
		setSelectedDocuments((prev) => prev.filter((item) => item.id !== document.id));

		const fileName = getDocumentName(document).trim();
		if (!fileName) {
			return;
		}
		const escapedName = fileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
		const nextText = messageText
			.replace(new RegExp(`(^|\\s)${escapedName}(?=\\s|$)`, 'g'), ' ')
			.replace(/\s{2,}/g, ' ')
			.trimStart();
		setMessageText(nextText);
	};

	useEffect(() => {
		if (!mentionAnchorVisible || !mentionListRef.current) {
			return;
		}

		const activeEl = mentionListRef.current.querySelector(
			`[data-mention-index="${activeMentionIndex}"]`
		) as HTMLElement | null;
		activeEl?.scrollIntoView({ block: 'nearest' });
	}, [activeMentionIndex, mentionAnchorVisible]);

	const handleScroll = () => {
		if (!listRef.current || isPaginating || isChatHistoryLoading || !hasMore) {
			return;
		}
		if (listRef.current.scrollTop <= 40) {
			void loadMessages(page + 1, 'paginate', selectedAddress.id);
		}
	};

	const handleSend = async (questionOverride?: string, predefinedQuestionKey?: string) => {
		const question = (questionOverride ?? messageText).trim();
		if (!question || !selectedAddress?.id || isSending || isAwaitingAnswer) {
			return;
		}
		if (predefinedQuestionKey) {
			setLoadingPredefinedQuestionKey(predefinedQuestionKey);
		}
		const selectedDocsAtSend = [...selectedDocuments];
		const documentIdsToSend = selectedDocsAtSend.map((doc) => doc.id);

		setMessageText('');
		setSelectedDocuments([]);
		setIsAwaitingAnswer(true);
		setPendingForAddress(selectedAddress.id, {
			question,
			startedAt: new Date().toISOString()
		});
		try {
			const tempId = `temp-${Date.now()}`;
			setMessages((prev) => [
				...prev,
				{
					id: tempId,
					role: 'user',
					content: question,
					createdAt: new Date().toISOString(),
					documents: selectedDocsAtSend
				}
			]);
			requestAnimationFrame(() => {
				if (listRef.current) {
					listRef.current.scrollTop = listRef.current.scrollHeight;
				}
			});

			const askResponse = await askChat({
				address_id: selectedAddress.id,
				question,
				...(documentIdsToSend.length > 0 ? { document_ids: documentIdsToSend } : {})
			}).unwrap();
			const answer = askResponse?.data?.answer || '';
			const questions = askResponse?.data?.predefined_questions || [];
			const nextConversationId = extractConversationId(askResponse) ?? conversationId;
			if (nextConversationId != null) {
				setConversationId(nextConversationId);
			}
			setPredefinedQuestions(Array.isArray(questions) ? questions.filter(Boolean) : []);
			if (answer) {
				const includesSupportEmail = answer.toLowerCase().includes(SUPPORT_EMAIL);
				const apiSupport = askResponse?.data?.support;
				const mailContext: SupportMailtoContext = {
					addressId: selectedAddress.id,
					conversationId: nextConversationId,
					question
				};

				setMessages((prev) => [
					...prev,
					{
						id: `assistant-${Date.now()}`,
						role: 'assistant',
						content: answer,
						createdAt: new Date().toISOString(),
						support: includesSupportEmail
							? {
									email: apiSupport?.email || SUPPORT_EMAIL,
									subject: apiSupport?.subject || SUPPORT_MAIL_SUBJECT,
									body: buildSupportMailBody(mailContext),
									mailto: buildSupportMailtoHref(mailContext)
								}
							: apiSupport
					}
				]);
			}
			setPendingForAddress(selectedAddress.id);
			requestAnimationFrame(() => {
				if (listRef.current) {
					listRef.current.scrollTop = listRef.current.scrollHeight;
				}
			});
		} catch (error) {
			const apiError = error as {
				data?: { errors?: Array<{ detail?: string; title?: string }> };
			};
			const errMsg =
				apiError?.data?.errors?.[0]?.detail ||
				apiError?.data?.errors?.[0]?.title ||
				'Failed to get chat response. Please try again.';
			toast.error(errMsg);
			setPendingForAddress(selectedAddress.id);
		} finally {
			setIsAwaitingAnswer(false);
			setLoadingPredefinedQuestionKey(null);
		}
	};

	const shouldShowPredefinedQuestions = !isChatHistoryLoading && predefinedQuestions.length > 0;

	const getTypeLabel = (type: string | null) => {
		if (!type) return 'N/A';
		return type.charAt(0).toUpperCase() + type.slice(1);
	};

	const getTypeColor = (type: string | null) => {
		if (!type) return 'default';
		const typeMap: Record<string, 'primary' | 'success' | 'warning'> = {
			apartment: 'primary',
			house: 'success',
			office: 'warning',
			residential: 'primary'
		};
		return typeMap[type] || 'default';
	};

	return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          maxWidth: "100vw",
          backgroundColor: "#e8e8e8",
        },
      }}
    >
      <Box className="h-full flex flex-col">
        <Box
          className="flex items-center justify-between px-4 py-3"
          sx={{
            backgroundColor: "#f5f5f5",
            color: (theme) => theme.palette.text.primary,
          }}
        >
          <Box className="flex items-center gap-2">
            <IconButton
              onClick={
                shouldShowAddressSelection
                  ? onClose
                  : canGoBackToAddressSelection
                    ? onBackToAddressSelection
                    : onClose
              }
              aria-label={shouldShowAddressSelection ? "close-chat-inbox" : "back-chat-inbox"}
            >
              <FuseSvgIcon size={18} sx={{ color: "inherit" }}>
                {shouldShowAddressSelection ? "heroicons-outline:x-mark" : "heroicons-outline:arrow-left"}
              </FuseSvgIcon>
            </IconButton>
			<Box className="flex flex-col">
				{shouldShowAddressSelection ? (
					<Typography
						variant="subtitle1"
						className="font-semibold"
						sx={{ lineHeight: 1.3 }}
						>
							Close
						</Typography>
					) : (
					<>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ lineHeight: 1.2 }}
						>
							Address Name
						</Typography>
						<Typography
							variant="subtitle1"
							className="font-semibold"
							sx={{ lineHeight: 1.3 }}
						>
							{addressText}
						</Typography>
					</>					
				)}
            </Box>
          </Box>
        </Box>
        <Divider />

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {shouldShowAddressSelection ? (
            <Box
              className="flex-1 overflow-auto px-4 py-4"
              sx={{ backgroundColor: "#f5f5f5" }}
            >
              <Box className="flex flex-col gap-4">
				{isAddressesLoading ? (
					<>
						<Box className="h-full min-h-[180px] flex flex-col items-center justify-center gap-1.5">
							<CircularProgress size={24} sx={{ color: "primary.main" }} />
							<Typography variant="body2" color="text.secondary">
							Verifying address to start chat...
							</Typography>
						</Box>
					</>
                ) : addresses.length === 0 ? (
                  <Box className="flex flex-col items-center justify-center py-8 gap-4">
                    <Typography
                      variant="body1"
                      className="text-gray-600 text-center"
                    >
                      There is no available address. Please add address first.
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={() => {
                        onClose();
                        navigate('/apps/addresses');
                      }}
                      startIcon={<FuseSvgIcon>heroicons-outline:plus</FuseSvgIcon>}
                    >
                      Add Address
                    </Button>
                  </Box>
				) : (
					<>							
						<Typography
							variant="body1"
							className="font-medium"
							>
							Select an address to start chat:
						</Typography>
						<Box className="grid grid-cols-1 sm:grid-cols-2 gap-3">
							{addresses.map((address) => (
							<Card
								key={address.id}
								title={address.location_name || 'N/A'}	
								sx={{
								border: '1px solid',
								borderColor: 'divider',
								'&:hover': {
									borderColor: 'primary.main',
									boxShadow: 2
								}
								}}
							>
								<CardActionArea onClick={() => onSelectAddress?.(address)}>
								<CardContent>
									<Typography
									variant="h6"
									className="font-semibold mb-1 text-lg"
									>
									{address.name || 'N/A'}
									</Typography>
									<Typography
									variant="body2"
									className="text-gray-600 mb-2 truncate line-clamp-1 text-ellipsis"
									>
									{address.location_name || 'N/A'}
									</Typography>
									<Chip
									label={getTypeLabel(address.type)}
									color={getTypeColor(address.type)}
									size="small"
									/>
								</CardContent>
								</CardActionArea>
							</Card>
							))}
						</Box>
					</>
                )}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
              <Box
                className="flex-1 overflow-auto px-4 py-4"
                ref={listRef}
                onScroll={handleScroll}
                sx={{ backgroundColor: "#f5f5f5" }}
              >
                {isChatHistoryLoading ? (
                  <Box className="h-full min-h-[180px] flex flex-col items-center justify-center gap-1.5">
                    <CircularProgress size={24} sx={{ color: "primary.main" }} />
                    <Typography variant="body2" color="text.secondary">
                      Loading chat…
                    </Typography>
                  </Box>
                ) : (
                  <Box className="space-y-3">
                    {isPaginating && (
                      <Box className="flex justify-center">
                        <CircularProgress size={18} />
                      </Box>
                    )}
                    {messages.map((message) => (
                        <Box
                          key={String(message.id)}
                          className={`max-w-[88%] ${message.role === "user" ? "ml-auto max-w-fit" : "mr-auto"}`}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            className={`block mb-1 px-1 ${message.role === "user" ? "text-right" : "text-left"}`}
                          >
                            {message.role === "user" ? "You" : "AI Assistant"}
                          </Typography>
                          <Box
                            className={`px-3 py-2 rounded-lg ${
                              message.role === "user"
                                ? "border border-solid"
                                : "bg-white border border-solid"
                            }`}
                            sx={
                              message.role === "user"
                                ? {
                                    backgroundColor: (theme) =>
                                      theme.palette.mode === "light"
                                        ? "rgba(255, 235, 59, 0.22)"
                                        : "rgba(255, 235, 59, 0.18)",
                                    color: "#000",
                                    borderColor: "rgba(0, 0, 0, 0.22)",
                                  }
                                : {
                                    color: "#000",
                                    borderColor: "rgba(0, 0, 0, 0.22)",
                                  }
                            }
                          >
                            {message.role === "assistant"
                              ? renderAssistantMessageContent(message)
                              : renderUserMessageContent(message)}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            className={`block mt-1 px-1 ${message.role === "user" ? "text-right" : "text-left"}`}
                          >
                            {formatMessageTime(message.createdAt)}
                          </Typography>
                        </Box>
					))}
					{isAwaitingAnswer && (
                          <Box
						  className="mt-1 inline-flex items-center gap-2 px-1"
						  sx={{
							"@keyframes aiDotWave": {
							  "0%, 80%, 100%": { transform: "translateY(0)", opacity: 0.45 },
							  "40%": { transform: "translateY(-5px)", opacity: 1 },
							},
						  }}
						>
						  <Box className="inline-flex items-end gap-1">
							{[0, 1, 2].map((dotIndex) => (
							  <Box
								key={dotIndex}
								sx={{
								  width: 6,
								  height: 6,
								  borderRadius: "50%",
								  backgroundColor: "#facc15",
								  animation: "aiDotWave 1s ease-in-out infinite",
								  animationDelay: `${dotIndex * 0.14}s`,
								  boxShadow: "0 0 8px rgba(250, 204, 21, 0.45)",
								}}
							  />
							))}
						  </Box>
						  <Typography
							variant="caption"
							sx={{ color: "text.secondary", fontWeight: 500 }}
						  >
							AI Assistant is generating...
						  </Typography>
						</Box>							
					)}
                    {shouldShowPredefinedQuestions && (
                      <Box
                        className={`flex flex-col gap-2 ${messages.length > 0 || isAwaitingAnswer ? "pt-4 !mt-3 border-t border-solid" : "pt-1 pb-1"}`}
                        sx={{
                          borderColor: "rgba(0, 0, 0, 0.12)",
                        }}
                      >
                        {predefinedQuestions.map((question, index) => {
                          const questionKey = `${question}-${index}`;
                          const isGenerating = isSending || isAwaitingAnswer;
                          const isSelectedQuestionLoading =
                            loadingPredefinedQuestionKey === questionKey &&
                            isGenerating;

							return (
								<Box
									key={questionKey}
									component="button"
									type="button"
									disabled={isGenerating}
									onClick={() => {
									if (isSending || isAwaitingAnswer) {
										return;
									}
									void handleSend(question, questionKey);
									}}
									sx={{
									width: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									gap: 1,
									textAlign: "left",
									px: 1.4,
									py: 1,
									borderRadius: 2,
									backgroundColor: isSelectedQuestionLoading
										? "rgba(239,246,255,1)"
										: "#ffffff",
									border: isSelectedQuestionLoading
										? "1px solid rgba(37,99,235,0.42)"
										: "1px solid rgba(37,99,235,0.12)",
									boxShadow: isSelectedQuestionLoading
										? "0 2px 8px rgba(37,99,235,0.14)"
										: "0 1px 3px rgba(0,0,0,0.05)",
									fontSize: "12.5px",
									fontWeight: 500,
									lineHeight: 1.4,
									color: isGenerating && !isSelectedQuestionLoading
										? "rgba(17,24,39,0.58)"
										: "#111827",
									transition: "all 0.14s ease",
									cursor: isGenerating ? "not-allowed" : "pointer",
									"&:hover": {
										backgroundColor: "rgba(239,246,255,1)",
										borderColor: "rgba(37,99,235,0.32)",
										boxShadow: "0 2px 8px rgba(37,99,235,0.12)",
									},
									"&:disabled": {
										opacity: 1,
										cursor: "not-allowed",
									},
									}}
								>
									<span>{question}</span>
									{/* {isSelectedQuestionLoading && <CircularProgress size={14} />}*/}
								</Box>
                          	);
                        })}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              <Divider />
              <Box className="p-3" sx={{ backgroundColor: "#f5f5f5" }}>
            {mentionAnchorVisible && filteredMentionDocuments.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  mb: 1,
                  maxHeight: 180,
                  overflowY: "auto",
                  borderRadius: 1.5,
                  border: "1px solid rgba(0,0,0,0.12)",
                }}
              >
                <List dense disablePadding ref={mentionListRef}>
                  {filteredMentionDocuments.map((document, index) => (
                    <ListItemButton
                      key={document.id}
                      data-mention-index={index}
                      onClick={() => handleSelectMentionDocument(document)}
                      selected={
                        filteredMentionDocuments[activeMentionIndex]?.id ===
                        document.id
                      }
                      sx={{ py: 0.5, px: 1.25 }}
                    >
                      <ListItemText
                        primary={document.file_name}
                        secondary={document.file_type?.toUpperCase()}
                        primaryTypographyProps={{
                          variant: "caption",
                          sx: {
                            color: "#000",
                            fontSize: "12px",
                            lineHeight: 1.3,
                          },
                        }}
                        secondaryTypographyProps={{
                          variant: "caption",
                          sx: { fontSize: "10px", lineHeight: 1.2 },
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            )}

            <Box
              className="chatbox-input-container"
              sx={{
                border: "1px solid rgba(43, 127, 255, 0.18)",
                backgroundColor: "#fff",
                borderRadius: "8px",
                px: 1,
                py: 0.75,
                pb: 1.2,
                boxShadow: isComposerActive
                  ? CHATBOX_INPUT_SHADOW_ACTIVE
                  : CHATBOX_INPUT_SHADOW_IDLE,
                transition: "box-shadow 0.28s ease",
              }}
            >
              {selectedDocuments.length > 0 && (
                <Box className="flex flex-wrap gap-2 mb-1">
                  {selectedDocuments.map((document) => (
                    <Box
                      key={document.id}
                      className="inline-flex items-center gap-1"
                      sx={{
                        px: 0.75,
                        py: 0,
                        borderRadius: "8px",
                        backgroundColor: "#d8e5f6",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                        maxWidth: "100%",
                        transition: "all 0.18s ease",
                        "&:hover": {
                          boxShadow: "0 1px 5px rgba(0,0,0,0.35)",
                          "& .chat-inline-paper-clip": {
                            color: "rgba(37, 99, 235, 0.95)",
                            transform: "scale(1.06)",
                          },
                        },
                      }}
                    >
                      <FuseSvgIcon
                        className="chat-inline-paper-clip"
                        size={13}
                        sx={{
                          color: "rgba(0, 0, 0, 0.6)",
                          flexShrink: 0,
                          transition: "color 0.18s ease, transform 0.18s ease",
                        }}
                      >
                        heroicons-outline:paper-clip
                      </FuseSvgIcon>
                      <Box
                        component={document.document_file_url ? "a" : "span"}
                        href={document.document_file_url || undefined}
                        target={
                          document.document_file_url ? "_blank" : undefined
                        }
                        rel={
                          document.document_file_url
                            ? "noopener noreferrer"
                            : undefined
                        }
                        sx={{
                          fontSize: "10px",
                          lineHeight: 1,
                          fontWeight: 500,
                          color: "#1f2937",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: { xs: 130, sm: 170 },
                          border: "0 !important",
                          textDecoration: "none !important",
                          backgroundColor: "transparent !important",
                          cursor: document.document_file_url
                            ? "pointer"
                            : "default",
                        }}
                      >
                        {document.file_name}
                      </Box>
                      <Tooltip title="Remove document" placement="top" arrow>
                        <IconButton
                          size="small"
                          aria-label={`remove-${document.id}`}
                          onClick={() => handleRemoveSelectedDocument(document)}
                          sx={{
                            p: 0.2,
                            ml: 0.25,
                            mr: "-5px",
                            color: "rgba(0,0,0,0.55)",
                            width: "25px",
                            height: "25px",
                            borderRadius: 0,
                            "&:hover": {
                              color: "rgba(0,0,0,0.8)",
                              backgroundColor: "#b1c3da",
                            },
                          }}
                        >
                          <FuseSvgIcon size={12}>
                            heroicons-outline:x-mark
                          </FuseSvgIcon>
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}

              <TextField
                fullWidth
                variant="standard"
                disabled={!selectedAddress}
                placeholder="Type your message... (use @ to search documents)"
                value={messageText}
                multiline
                minRows={1}
                maxRows={4}
                onChange={(event) => setMessageText(event.target.value)}
                onFocus={() => setIsComposerFocused(true)}
                onBlur={() => setIsComposerFocused(false)}
                onKeyDown={(event) => {
                  if (
                    mentionAnchorVisible &&
                    filteredMentionDocuments.length > 0
                  ) {
                    if (event.key === "ArrowDown") {
                      event.preventDefault();
                      setActiveMentionIndex((prev) =>
                        prev >= filteredMentionDocuments.length - 1
                          ? 0
                          : prev + 1,
                      );
                      return;
                    }
                    if (event.key === "ArrowUp") {
                      event.preventDefault();
                      setActiveMentionIndex((prev) =>
                        prev <= 0
                          ? filteredMentionDocuments.length - 1
                          : prev - 1,
                      );
                      return;
                    }
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      handleSelectMentionDocument(
                        filteredMentionDocuments[activeMentionIndex],
                      );
                      return;
                    }
                  }

                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void handleSend();
                  }
                }}
                InputProps={{ disableUnderline: true }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#000",
                    p: 0,
                    lineHeight: 1.45,
                  },
                }}
              />

              <Box className="mt-1 flex items-center justify-end gap-1">
                <IconButton
                  size="small"
                  disabled={!selectedAddress}
                  aria-label="attach-document"
                  onClick={() => setUploadDialogOpen(true)}
                  sx={COMPOSER_ACTION_ICON_BUTTON_SX}
                >
                  <FuseSvgIcon size={16} sx={{ color: "inherit" }}>
                    heroicons-outline:paper-clip
                  </FuseSvgIcon>
                </IconButton>
                <IconButton
                  size="small"
                  disabled={
                    !selectedAddress ||
                    !messageText.trim() ||
                    isSending ||
                    isAwaitingAnswer
                  }
                  aria-label="send-message"
                  onClick={() => {
                    void handleSend();
                  }}
                  sx={COMPOSER_ACTION_ICON_BUTTON_SX}
                >
                  <FuseSvgIcon size={16} sx={{ color: "inherit" }}>
                    heroicons-outline:paper-airplane
                  </FuseSvgIcon>
                </IconButton>
              </Box>
              </Box>
            </Box>
            </Box>
          )}
        </Box>
      </Box>
      <UploadDocumentDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        preselectedAddressId={selectedAddress?.id ?? null}
        lockAddressSelection={true}
        allowMultipleFiles={false}
        onUploadedSuccess={({ addressId, fileNames, documentIds }) => {
          setUploadDialogOpen(false);
          if (selectedAddress?.id === addressId) {
            setPendingUploadedFiles(fileNames);
            setPendingUploadedDocIds(documentIds);
            setUploadedFilesPollStartedAt(Date.now());
            hasShownUploadPendingToastRef.current = false;
            void refetchDocuments();
          }
        }}
      />
    </Drawer>
  );
}

export default ChatBoxDialog;

